/**
 * useCreateCategory Hook
 *
 * Creates a new category using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import { extractGraphQLErrorMessage } from "@/lib/graphql-utils";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const CreateCategoryDocument = `
  mutation CreateCategory($input: CategoryInput!) {
    createCategory(input: $input) { id }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

export interface CategoryInput {
  code: string;
  name: string;
  description: string | null;
  color: string | null;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
}

interface UseCreateCategoryResult {
  createCategory: (input: CategoryInput) => Promise<string>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Create a new category.
 */
export function useCreateCategory(): UseCreateCategoryResult {
  const [result, executeMutation] = useMutation<
    { createCategory: { id: string } },
    { input: CategoryInput }
  >(CreateCategoryDocument);

  const createCategory = useCallback(
    async (input: CategoryInput): Promise<string> => {
      try {
        const response = await executeMutation({ input });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Failed to create category");
          throw new Error(message);
        }

        if (!response.data?.createCategory?.id) {
          throw new Error("No category ID returned");
        }

        errorTracking.addBreadcrumb({
          category: "admin",
          message: "Category created",
          data: { categoryId: response.data.createCategory.id },
        });

        return response.data.createCategory.id;
      } catch (err) {
        const createError =
          err instanceof Error ? err : new Error("Failed to create category");

        errorTracking.captureError(createError, {
          tags: { feature: "admin", action: "createCategory" },
        });

        throw createError;
      }
    },
    [executeMutation]
  );

  return {
    createCategory,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
